import { useState, useEffect, useCallback } from "react";
import { Loader2, Save, Plus, X, Clock, Calendar, MapPin, Truck, Sparkles, Package, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_FULL = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" };

const TYPE_META = {
  pickup: { icon: MapPin, color: "#22c55e", label: "Pick-up", desc: "Customer collects outfit before event" },
  delivery: { icon: Truck, color: "#3b82f6", label: "Delivery", desc: "You deliver outfit to customer" },
  trial: { icon: Sparkles, color: "#a855f7", label: "Trial", desc: "Admin-managed trial fittings" },
  dropoff: { icon: Package, color: "#f97316", label: "Drop-off", desc: "Customer returns outfit after event" },
};

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-[#9A7B4F]" : "bg-[#282828]/15"}`}
      data-testid={testId}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function TimeRangeRow({ range, onChange, onRemove, idx }) {
  return (
    <div className="flex items-center gap-2 py-1.5" data-testid={`time-range-${idx}`}>
      <input type="time" value={range.start} onChange={e => onChange({ ...range, start: e.target.value })}
        className="border border-[#282828]/12 px-2 py-1.5 text-xs bg-white focus:border-[#9A7B4F] focus:outline-none w-[100px]" data-testid={`range-start-${idx}`} />
      <span className="text-[#676664]/40 text-xs">to</span>
      <input type="time" value={range.end} onChange={e => onChange({ ...range, end: e.target.value })}
        className="border border-[#282828]/12 px-2 py-1.5 text-xs bg-white focus:border-[#9A7B4F] focus:outline-none w-[100px]" data-testid={`range-end-${idx}`} />
      <div className="flex items-center gap-1 ml-2">
        <label className="text-[10px] text-[#676664]/50 uppercase tracking-wider">Cap</label>
        <input type="number" min={1} value={range.capacity} onChange={e => onChange({ ...range, capacity: Math.max(1, parseInt(e.target.value) || 1) })}
          className="border border-[#282828]/12 px-2 py-1.5 text-xs bg-white focus:border-[#9A7B4F] focus:outline-none w-14 text-center" data-testid={`range-cap-${idx}`} />
      </div>
      <button onClick={onRemove} className="p-1 text-[#676664]/30 hover:text-red-400 ml-auto" data-testid={`remove-range-${idx}`}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function DayScheduleRow({ day, config, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const ranges = config.time_ranges || [];

  const updateField = (field, value) => onChange({ ...config, [field]: value });
  const updateRange = (idx, newRange) => {
    const updated = [...ranges];
    updated[idx] = newRange;
    onChange({ ...config, time_ranges: updated });
  };
  const addRange = () => {
    onChange({ ...config, time_ranges: [...ranges, { start: "09:00", end: "17:00", capacity: 2 }] });
    setExpanded(true);
  };
  const removeRange = (idx) => onChange({ ...config, time_ranges: ranges.filter((_, i) => i !== idx) });

  return (
    <div className={`border-b border-[#282828]/5 last:border-0 ${!config.enabled ? "opacity-50" : ""}`} data-testid={`day-${day}`}>
      <div className="flex items-center gap-3 py-2.5 px-3">
        <Toggle checked={config.enabled} onChange={v => updateField("enabled", v)} testId={`toggle-day-${day}`} />
        <span className="text-[13px] text-[#282828] font-medium w-24">{DAY_FULL[day]}</span>

        {config.enabled && ranges.length > 0 && (
          <div className="flex-1 flex items-center gap-2 overflow-hidden">
            {ranges.map((r, i) => (
              <span key={i} className="text-[11px] text-[#676664]/60 bg-[#FAF8F0] px-2 py-0.5 border border-[#282828]/6 whitespace-nowrap">
                {r.start}–{r.end} ({r.capacity})
              </span>
            ))}
          </div>
        )}

        {config.enabled && (
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => setExpanded(!expanded)} className="p-1 text-[#676664]/40 hover:text-[#9A7B4F]">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>

      {config.enabled && expanded && (
        <div className="pb-3 px-3 pl-16">
          {ranges.map((r, i) => (
            <TimeRangeRow key={i} idx={i} range={r} onChange={nr => updateRange(i, nr)} onRemove={() => removeRange(i)} />
          ))}
          <button onClick={addRange} className="flex items-center gap-1 px-3 py-1.5 mt-1 text-[10px] text-[#9A7B4F] border border-dashed border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5" data-testid={`add-range-${day}`}>
            <Plus className="w-3 h-3" /> Add Time Range
          </button>
        </div>
      )}
    </div>
  );
}

function BookingTypeCard({ typeKey, config, durations, onChange, onToggle, onDurationChange }) {
  const [expanded, setExpanded] = useState(false);
  const meta = TYPE_META[typeKey] || { icon: Calendar, color: "#9A7B4F", label: typeKey, desc: "" };
  const Icon = meta.icon;
  const schedule = config.weekly_schedule || {};

  const updateScheduleDay = (day, dayConfig) => onChange({ ...config, weekly_schedule: { ...schedule, [day]: dayConfig } });
  const enabledDays = DAYS.filter(d => schedule[d]?.enabled).length;

  return (
    <div className="bg-white border border-[#282828]/8" data-testid={`booking-type-${typeKey}`}>
      <div className="flex items-center gap-4 px-5 py-4 border-b border-[#282828]/5">
        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}30` }}>
          <Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] text-[#282828] font-medium" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{meta.label}</h3>
          <p className="text-[11px] text-[#676664]/50">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-3">
          {config.enabled && <span className="text-[10px] text-[#676664]/40">{enabledDays} days · {durations[typeKey] || 60}m</span>}
          <Toggle checked={config.enabled} onChange={onToggle} testId={`toggle-${typeKey}`} />
        </div>
      </div>

      {config.enabled && (
        <>
          <div className="grid grid-cols-2 gap-4 px-5 py-3 border-b border-[#282828]/5">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Duration (minutes)</label>
              <input type="number" min={15} step={15}
                value={durations[typeKey] || 60}
                onChange={e => onDurationChange(typeKey, parseInt(e.target.value) || 60)}
                className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                data-testid={`duration-${typeKey}`} />
            </div>
            {/* Patch 2.2 (Issue 4): Trial visibility is now driven exclusively
                by Trial Booking Mode below — the generic Customer Bookable
                toggle would mislead the admin into thinking they can layer
                extra availability on top of the trial calendar. Pickup /
                Drop-off / Delivery still expose the toggle unchanged. */}
            {typeKey === "trial" ? (
              <div className="text-[11px] text-[#676664]/50 leading-relaxed self-end" data-testid="trial-bookable-explainer">
                Trial visibility is controlled by <strong>Trial Booking Mode</strong> below — the generic Customer Bookable toggle does not apply here.
              </div>
            ) : (
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-[12px] text-[#676664]">Customer Bookable</span>
                  <p className="text-[10px] text-[#676664]/40">
                    {config.customer_bookable ? "Customers can book directly" : "Admin-only (enquiry form)"}
                  </p>
                </div>
                <Toggle checked={config.customer_bookable ?? true} onChange={v => onChange({ ...config, customer_bookable: v })} testId={`toggle-bookable-${typeKey}`} />
              </div>
            )}
          </div>

          {/* Trial Booking System — Patch 1: mode + fee admin controls. */}
          {typeKey === "trial" && (
            <div className="px-5 py-4 border-b border-[#282828]/5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Trial Booking Mode</label>
                <select
                  value={config.booking_mode || "enquiry"}
                  onChange={e => onChange({ ...config, booking_mode: e.target.value })}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="trial-booking-mode"
                >
                  <option value="enquiry">Enquiry — customer submits request, admin confirms</option>
                  <option value="direct">Direct Booking — customer books an open slot instantly</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[12px] text-[#676664]">Charge for trial booking</span>
                  <p className="text-[10px] text-[#676664]/40">
                    {config.trial_fee_enabled ? "Trial fee is collected before the slot is confirmed" : "Trial bookings are free"}
                  </p>
                </div>
                <Toggle checked={!!config.trial_fee_enabled} onChange={v => onChange({ ...config, trial_fee_enabled: v })} testId="toggle-trial-fee" />
              </div>
              {config.trial_fee_enabled && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Trial booking fee (AUD)</label>
                  <input
                    type="number" min={0} step={5}
                    value={config.trial_fee_amount ?? 0}
                    onChange={e => onChange({ ...config, trial_fee_amount: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                    data-testid="trial-fee-amount"
                  />
                </div>
              )}

              {/* Patch 3.1 — admin-configurable text for calendar dates that
                  carry no bookable trial slots. Display-only. */}
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">
                  Unavailable date label
                </label>
                <input
                  type="text" maxLength={40}
                  placeholder="Unavailable"
                  value={config.unavailable_date_label ?? ""}
                  onChange={e => onChange({ ...config, unavailable_date_label: e.target.value })}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="trial-unavailable-label"
                />
                <p className="text-[10px] text-[#676664]/40 mt-1">
                  Shown on customer calendar for dates with no bookable slots. Examples: <em>Booked Out</em>, <em>Not Available</em>, <em>No Appointments</em>. Leave blank to use &ldquo;Unavailable&rdquo;.
                </p>
              </div>
            </div>
          )}

          <div className="px-5 py-3 border-b border-[#282828]/5">
            <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-[12px] text-[#9A7B4F] hover:text-[#8B6B3F]" data-testid={`expand-schedule-${typeKey}`}>
              <Calendar className="w-3.5 h-3.5" />
              {expanded ? "Hide" : "Configure"} Weekly Schedule
              {typeKey === "trial" && (
                <span className="ml-2 text-[10px] text-[#676664]/60 normal-case">
                  {config.booking_mode === "direct"
                    ? "(informational only — Direct Mode uses Trial Availability slots)"
                    : "(applies to Enquiry Mode)"}
                </span>
              )}
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
          </div>

          {expanded && (
            <div>
              {DAYS.map(day => (
                <DayScheduleRow key={day} day={day}
                  config={schedule[day] || { enabled: false, repeat_weekly: true, time_ranges: [] }}
                  onChange={dc => updateScheduleDay(day, dc)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminBookingTypes() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/booking/admin/settings`, { headers: headers() });
        setSettings(res.data);
      } catch {
        toast.error("Failed to load booking type settings");
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setWarnings([]);
    try {
      const res = await axios.put(`${API}/booking/admin/settings`, settings, { headers: headers() });
      if (res.data.warnings?.length) {
        setWarnings(res.data.warnings);
        toast.warning(`Saved with ${res.data.warnings.length} warning(s)`);
      } else {
        toast.success("Booking type settings saved");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail.errors) toast.error(detail.errors.join("\n"));
      else toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  const updateBookingType = useCallback((typeKey, newConfig) => {
    setSettings(prev => ({ ...prev, booking_types: { ...prev.booking_types, [typeKey]: newConfig } }));
  }, []);

  const toggleBookingType = useCallback((typeKey, enabled) => {
    setSettings(prev => ({
      ...prev,
      booking_types: { ...prev.booking_types, [typeKey]: { ...prev.booking_types[typeKey], enabled } },
    }));
  }, []);

  const updateGlobal = useCallback((field, value) => {
    setSettings(prev => ({ ...prev, global: { ...prev.global, [field]: value } }));
  }, []);

  const updateDuration = useCallback((typeKey, value) => {
    setSettings(prev => ({
      ...prev,
      global: {
        ...prev.global,
        durations: { ...(prev.global?.durations || {}), [typeKey]: value },
      },
    }));
  }, []);

  if (loading) return (
    <AdminLayout title="Booking Types" subtitle="Configure booking types and schedules">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
    </AdminLayout>
  );
  if (!settings) return (
    <AdminLayout title="Booking Types" subtitle="Configure booking types and schedules">
      <p className="text-center py-10 text-[#676664]/50">Unable to load settings</p>
    </AdminLayout>
  );

  const g = settings.global || {};
  const types = settings.booking_types || {};
  const durations = g.durations || {};

  return (
    <AdminLayout
      title="Booking Types"
      subtitle="Configure pickup, delivery, trial & drop-off schedules"
      actions={
        <Button onClick={handleSave} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-6" data-testid="save-booking-types-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      }
    >
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 mb-4" data-testid="settings-warnings">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[12px] text-amber-700 font-medium mb-1">Saved with warnings:</p>
              {warnings.map((w, i) => <p key={i} className="text-[11px] text-amber-600">{w}</p>)}
            </div>
          </div>
        </div>
      )}

      {/* Global Settings */}
      <div className="bg-white border border-[#282828]/8 p-5 mb-4" data-testid="global-settings">
        <h3 className="text-[15px] text-[#282828] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
          <Clock className="w-4 h-4 inline mr-2 text-[#9A7B4F]" strokeWidth={1.5} />
          Global Settings
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Slot Interval (min)</label>
            <input type="number" min={10} step={5} value={g.slot_interval_minutes || 30}
              onChange={e => updateGlobal("slot_interval_minutes", parseInt(e.target.value) || 30)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="global-interval" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Buffer Between (min)</label>
            <input type="number" min={0} step={5} value={g.buffer_minutes || 0}
              onChange={e => updateGlobal("buffer_minutes", parseInt(e.target.value) || 0)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="global-buffer" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Travel Buffer — Delivery (min)</label>
            <input type="number" min={0} step={5} value={g.travel_buffer_minutes ?? 30}
              onChange={e => updateGlobal("travel_buffer_minutes", parseInt(e.target.value) || 0)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="global-travel-buffer" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Delivery Radius (km)</label>
            <input type="number" min={1} step={1} value={g.delivery_radius_km ?? 15}
              onChange={e => updateGlobal("delivery_radius_km", parseInt(e.target.value) || 1)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="global-delivery-radius" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Slot Hold (seconds)</label>
            <input type="number" min={30} max={600} step={30} value={g.hold_seconds ?? 120}
              onChange={e => updateGlobal("hold_seconds", parseInt(e.target.value) || 120)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="global-hold-seconds" />
            <p className="text-[10px] text-[#676664]/40 mt-1">30–600s. Used to reserve a slot while customer confirms.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Trial Response (hrs)</label>
            <input type="number" min={1} value={g.trial_response_hours || 24}
              onChange={e => updateGlobal("trial_response_hours", parseInt(e.target.value) || 24)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="global-response-hours" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Business Lat,Lng</label>
            <input type="text"
              value={`${g.business_location?.lat || ""},${g.business_location?.lng || ""}`}
              onChange={e => {
                const [lat, lng] = e.target.value.split(",").map(s => parseFloat(s.trim()));
                updateGlobal("business_location", { lat: lat || 0, lng: lng || 0 });
              }}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
              data-testid="global-business-location" />
            <p className="text-[10px] text-[#676664]/40 mt-1">Origin for delivery distance calc.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Timezone</label>
            <input type="text" value={g.timezone || "Australia/Melbourne"} readOnly
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs bg-[#FAF8F0] text-[#676664]/60" />
          </div>
        </div>
      </div>

      {/* Booking Type Cards */}
      <div className="space-y-4">
        {["pickup", "delivery", "trial", "dropoff"].map(typeKey => (
          <BookingTypeCard
            key={typeKey}
            typeKey={typeKey}
            config={types[typeKey] || { enabled: false }}
            durations={durations}
            onChange={c => updateBookingType(typeKey, c)}
            onToggle={v => toggleBookingType(typeKey, v)}
            onDurationChange={updateDuration}
          />
        ))}
      </div>
    </AdminLayout>
  );
}
